import { Check as CheckIcon, Search } from "@mui/icons-material";
import { InputAdornment, TextField } from "@mui/material";
import keys from "locale/keys";
import React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Button from "../../../components/button/index";
import ConfirmationDialog from "../../../components/confirmationdialog";
import PreferNotToAnswerDialog from "../../../components/prefernottoanswerdialog";
import { State } from "../../../data/reducers/state";
import {
  assignSurveysToUserApi,
  getInitialSurveyQuestionsApi,
  getSurveyListWithRetryApi,
  submitSurveyProfilerApi,
} from "../../../data/slices/initialsurvey/api";
import { InitialSurveyQuestion } from "../../../data/slices/initialsurvey/interface";
import { UserDetailsStateProps } from "../../../data/slices/userdetails/interface";
import { WalletDetailsStateProps } from "../../../data/slices/wallet/interface";
import {
  DASHBOARD_ROUTES,
  PREFER_NOT_TO_ANSWER_OPTIONS,
  STORAGE_IS_INITIAL_SURVEY,
  STORAGE_USER_ID,
} from "../../../utils/constants";
import { initialSurveyMapper } from "../../../utils/mappers/InitialSurveyMapper";
import { getFromLocalStorage } from "../../../utils/Utils";
import { getUserName, walletBalance } from "../../../utils/Utils";
import WelcomeHeader from "../Header";
import { Wrapper } from "../styled.components";
import {
  AllOptionsSelectedMessage,
  ButtonContainer,
  CheckBox,
  ChipsContainer,
  EachOption,
  HeadingContainer,
  OptionsContainer,
  RadioBox,
  SearchContainer,
  SelectedChip,
  SurveyContainer,
  SurveyQuestions,
  SurveyQuestionsContainer,
} from "./styled.components";

interface InitialSurveyContainerProps {
  updateSidedrawerItem?: (index?: number) => void;
}

const InitialSurveyContainer = (props: InitialSurveyContainerProps) => {
  const { t } = useTranslation();
  const userId = getFromLocalStorage(STORAGE_USER_ID);

  // Get user details for the header
  const { details } = useSelector<State, UserDetailsStateProps>(
    (state) => state.userdetails,
  );

  const { walletdetails } = useSelector<State, WalletDetailsStateProps>(
    (state) => state.walletdetails,
  );

  const [showSurveyDialog, setShowSurveyDialog] = useState(true);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showPreferNotToAnswerDialog, setShowPreferNotToAnswerDialog] =
    useState(false);
  const [finalAnswers, setFinalAnswers] = useState({});

  // State for tracking current question and UI
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState(0);

  // Temporary state for tracking all selections (updated on selection click)
  const [tempSelections, setTempSelections] = useState({});

  const [surveyQuestions, setSurveyQuestions] = useState<
    InitialSurveyQuestion[]
  >([]);
  const [totalActiveQuestions, setTotalActiveQuestions] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Search functionality state
  const [searchTerm, setSearchTerm] = useState("");

  const handleStartSurvey = () => {
    setShowSurveyDialog(false);
  };

  const handleGoSurveyPage = () => {
    navigate(`/dashboard/${DASHBOARD_ROUTES.TASKS}`);
  };

  const navigate = useNavigate();

  const handleSurveyCompletion = () => {
    // Call assignSurveysToUser API first, then call USER_SURVEY after completion
    assignSurveysToUserApi(userId)
      .then(() => {
        // After assignSurveysToUser completes, call getSurveyList API with retry
        return getSurveyListWithRetryApi(3, 1000);
      })
      .catch((error) => {
        console.error("Error in survey completion flow:", error);
      });
  };

  useEffect(() => {
    const fetchSurveyQuestions = async () => {
      try {
        const response = await getInitialSurveyQuestionsApi(userId);
        const data = response?.data;
        // If we have totalAnsweredQuestions in the response, update our state
        if (data?.totalAnsweredQuestions !== undefined) {
          setCompletedQuestions(data.totalAnsweredQuestions);
        }
        // If we have totalActiveQuestions in the response, update our state
        if (data?.totalActiveQuestions !== undefined) {
          setTotalActiveQuestions(data.totalActiveQuestions);
        }
        const mappedQuestions = initialSurveyMapper(data?.Questions);
        setSurveyQuestions(mappedQuestions);

        if (!mappedQuestions || mappedQuestions.length === 0) {
          localStorage.setItem(STORAGE_IS_INITIAL_SURVEY, "true");
          navigate(`/dashboard/${DASHBOARD_ROUTES.TASKS}`);
        }
      } catch (error) {
        console.error("Error fetching survey questions:", error);
      }
    };

    fetchSurveyQuestions();

    // When navigating away from the page, check if there's any incomplete survey data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const currentQuestion = surveyQuestions[currentQuestionIndex];

  const isPreferNotToAnswerOption = (answerText) =>
    PREFER_NOT_TO_ANSWER_OPTIONS.indexOf(
      (answerText || "").trim().toLowerCase(),
    ) !== -1;

  // Whether the currently selected answer(s) for this question are a
  // "prefer not to answer" style option
  const isPreferNotToAnswerSelected = () => {
    if (!currentQuestion || !currentQuestion.answers) return false;
    const selection = tempSelections[currentQuestion.id];
    if (selection === undefined || selection === null) return false;
    const selectedIds = Array.isArray(selection) ? selection : [selection];

    return currentQuestion.answers.some(
      (answer) =>
        selectedIds.includes(answer.id) &&
        isPreferNotToAnswerOption(answer.text),
    );
  };

  // Handle selection click - only updates the temporary state
  const handleSelection = (answerId) => {
    if (currentQuestion.type === "Multi") {
      // For multi-select questions, toggle the selection in an array
      const currentSelections = tempSelections[currentQuestion.id] || [];
      const newSelections = currentSelections.includes(answerId)
        ? currentSelections.filter((id) => id !== answerId) // Remove if already selected
        : [...currentSelections, answerId]; // Add if not selected

      setTempSelections({
        ...tempSelections,
        [currentQuestion.id]: newSelections,
      });
    } else {
      // For single-select questions, just store the ID
      setTempSelections({
        ...tempSelections,
        [currentQuestion.id]: answerId,
      });
    }
  };

  const handleDismissPreferNotToAnswer = () => {
    setShowPreferNotToAnswerDialog(false);
  };

  const handleContinuePreferNotToAnswer = () => {
    setShowPreferNotToAnswerDialog(false);
    submitCurrentAnswer();
  };

  const handleChangePreferNotToAnswer = () => {
    setShowPreferNotToAnswerDialog(false);
    if (!currentQuestion || !currentQuestion.answers) return;

    const preferNotToAnswerIds = currentQuestion.answers
      .filter((answer) => isPreferNotToAnswerOption(answer.text))
      .map((answer) => answer.id);

    if (currentQuestion.type === "Multi") {
      const currentSelections = tempSelections[currentQuestion.id] || [];
      setTempSelections({
        ...tempSelections,
        [currentQuestion.id]: currentSelections.filter(
          (id) => !preferNotToAnswerIds.includes(id),
        ),
      });
    } else {
      const newSelections = { ...tempSelections };
      delete newSelections[currentQuestion.id];
      setTempSelections(newSelections);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const { id, type } = currentQuestion;

    if (type === "Number") {
      // Allow only numbers for number type
      const numericValue = value.replace(/[^0-9]/g, "");
      setTempSelections({
        ...tempSelections,
        [id]: numericValue,
      });
    } else {
      setTempSelections({ ...tempSelections, [id]: value });
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter answers based on search term
  const getFilteredAnswers = () => {
    if (!currentQuestion || !currentQuestion.answers) return [];

    if (!searchTerm.trim()) {
      return currentQuestion.answers;
    }

    return currentQuestion.answers.filter((answer) =>
      answer.text.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  // Get selected options for chip display
  const getSelectedOptions = () => {
    if (!currentQuestion || !currentQuestion.answers) return [];

    const selection = tempSelections[currentQuestion.id];
    if (!selection) return [];

    if (currentQuestion.type === "Multi") {
      return currentQuestion.answers.filter((answer) =>
        selection.includes(answer.id),
      );
    } else {
      return currentQuestion.answers.filter(
        (answer) => answer.id === selection,
      );
    }
  };

  // Handle chip deletion
  const handleChipDelete = (answerId) => {
    if (currentQuestion.type === "Multi") {
      const currentSelections = tempSelections[currentQuestion.id] || [];
      const newSelections = currentSelections.filter((id) => id !== answerId);

      setTempSelections({
        ...tempSelections,
        [currentQuestion.id]: newSelections,
      });
    } else {
      // For single select, clear the selection
      const newSelections = { ...tempSelections };
      delete newSelections[currentQuestion.id];
      setTempSelections(newSelections);
    }
  };

  // Truncate text for chip display
  const truncateText = (text, maxLength = 20) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Check if an answer is selected in the temporary state
  const isSelected = (answerId) => {
    const selection = tempSelections[currentQuestion.id];
    if (!selection) return false;

    if (currentQuestion.type === "Multi") {
      return selection.includes(answerId);
    } else {
      return selection === answerId;
    }
  };

  // Stores the final answer and submits it to the API
  const submitCurrentAnswer = async () => {
    try {
      setIsSubmitting(true);

      // const isLastQuestion = currentQuestionIndex === surveyQuestions.length - 1;

      // Store the current selection in the final answers
      const updatedAnswers = {
        ...finalAnswers,
        [currentQuestion.id]: tempSelections[currentQuestion.id],
      };

      setFinalAnswers(updatedAnswers);

      // Send responseText for Text/Number, answerIds for Single/Multi
      if (
        currentQuestion.type === "Text" ||
        currentQuestion.type === "Number"
      ) {
        await submitSurveyProfilerApi({
          userId,
          questionId: currentQuestion.id,
          responseText: tempSelections[currentQuestion.id],
          // isLastQuestion,
        });
      } else {
        const answerIds = Array.isArray(tempSelections[currentQuestion.id])
          ? tempSelections[currentQuestion.id]
          : [tempSelections[currentQuestion.id]];
        await submitSurveyProfilerApi({
          userId,
          questionId: currentQuestion.id,
          answerIds: answerIds,
          // isLastQuestion,
        });
      }

      if (currentQuestionIndex < surveyQuestions.length - 1) {
        // Move to the next question
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        // Update completed questions count - increment by 1
        setCompletedQuestions(completedQuestions + 1);
      } else {
        // If we're on the last question, mark the survey as completed
        setIsCompleted(true);
        // Set completed questions to the total active questions from API
        setCompletedQuestions(totalActiveQuestions);
        // Mark the initial survey as completed
        localStorage.setItem(STORAGE_IS_INITIAL_SURVEY, "true");
        // Here we calling the handleSurveyCompletion function for the first time user
        handleSurveyCompletion();
        setShowSuccessDialog(true);
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle next button click - shows the "prefer not to answer" nudge
  // instead of submitting when that option is currently selected
  const handleNext = () => {
    if (!currentQuestion) return;

    if (isPreferNotToAnswerSelected()) {
      setShowPreferNotToAnswerDialog(true);
      return;
    }

    submitCurrentAnswer();
  };

  // Initialize tempSelections with finalAnswers when the component mounts or question changes
  useEffect(() => {
    // Only run this effect if currentQuestion exists
    if (currentQuestion && finalAnswers[currentQuestion.id] !== undefined) {
      setTempSelections((prev) => ({
        ...prev,
        [currentQuestion.id]: finalAnswers[currentQuestion.id],
      }));
    }
    // Clear search term when question changes
    setSearchTerm("");
  }, [currentQuestionIndex, currentQuestion, finalAnswers]);

  return (
    <Wrapper data-testid="welcome-wrapper">
      <ConfirmationDialog
        show={showSurveyDialog}
        dismiss={() => {}} // Empty function as we're removing the dismiss button
        type={0}
        heading="Great! You've completed your basic info."
        msg={
          <>
            To unlock your personalised surveys and receive{" "}
            <span
              style={{
                fontWeight: "bold",
              }}
            >
              100 points
            </span>
            , please ensure the remaining questions are completed.
          </>
        }
        buttonText="Continue"
        onButtonClickHandler={handleStartSurvey}
        hideCloseIcon={true}
        hideIcon={true}
        headingAlign="left"
        messageAlign="left"
      />

      <ConfirmationDialog
        show={showSuccessDialog}
        dismiss={() => {}}
        type={0}
        heading="Thank you for completing your profile."
        msg={
          <>
            We’re pleased to let you know that{" "}
            <span
              style={{
                fontWeight: "bold",
              }}
            >
              100 points
            </span>{" "}
            will be added to your account as a reward.
          </>
        }
        buttonText="Get Started"
        onButtonClickHandler={handleGoSurveyPage}
        hideCloseIcon={true}
        hideIcon={true}
        headingAlign="left"
        messageAlign="left"
      />

      <PreferNotToAnswerDialog
        show={showPreferNotToAnswerDialog}
        dismiss={handleDismissPreferNotToAnswer}
        onChangeAnswerClick={handleChangePreferNotToAnswer}
        onContinueClick={handleContinuePreferNotToAnswer}
      />

      <WelcomeHeader
        step={currentQuestionIndex + 1} // Current question index (1-based)
        userName={getUserName(details?.firstName, details?.lastName)}
        walletBalance={walletBalance(walletdetails)}
        progress={
          totalActiveQuestions > 0
            ? (completedQuestions / totalActiveQuestions) * 100
            : 0
        }
        totalSteps={totalActiveQuestions > 0 ? totalActiveQuestions : 1} // Use totalActiveQuestions from API
        completedSteps={completedQuestions} // Pass the exact number of completed steps from the API
      />

      <SurveyContainer>
        <HeadingContainer></HeadingContainer>
        {!isCompleted && currentQuestion && (
          <>
            <SurveyQuestionsContainer>
              <SurveyQuestions>{currentQuestion.questionText}</SurveyQuestions>

              {/* Show search field only for Multi and Single choice questions */}
              {currentQuestion.type !== "Text" &&
                currentQuestion.type !== "Number" && (
                  <SearchContainer>
                    <TextField
                      size="small"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search Options"
                      variant="outlined"
                      sx={{ maxWidth: "550px", width: "100%" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </SearchContainer>
                )}

              {/* Show all options are selected */}
              {currentQuestion.type === "Multi" &&
                getSelectedOptions().length ===
                  currentQuestion.answers.length && (
                  <AllOptionsSelectedMessage>
                    <CheckIcon
                      color="success"
                      fontSize="small"
                      sx={{ marginRight: "10px" }}
                    />
                    <span>{t(keys.all_options_are_selected)}</span>
                  </AllOptionsSelectedMessage>
                )}

              {/* Show selected options as chips */}
              {currentQuestion.type === "Multi" &&
                getSelectedOptions().length > 0 &&
                getSelectedOptions().length !==
                  currentQuestion.answers.length && (
                  <ChipsContainer>
                    {getSelectedOptions().map((option) => (
                      <SelectedChip
                        key={option.id}
                        label={truncateText(option.text)}
                        onDelete={() => handleChipDelete(option.id)}
                        size="small"
                        variant="filled"
                      />
                    ))}
                  </ChipsContainer>
                )}

              <OptionsContainer>
                {currentQuestion.type === "Text" ||
                currentQuestion.type === "Number" ? (
                  <TextField
                    type={currentQuestion.type.toLowerCase()}
                    value={tempSelections[currentQuestion.id] || ""}
                    onChange={handleInputChange}
                    placeholder={t(keys.type_your_answer_here)}
                    fullWidth
                    variant="outlined"
                    sx={{ maxWidth: "350px" }}
                  />
                ) : (
                  getFilteredAnswers().map((answer) => (
                    <EachOption
                      key={answer.id}
                      onClick={() => handleSelection(answer.id)}
                    >
                      {currentQuestion.type === "Multi" ? (
                        <CheckBox checked={isSelected(answer.id)} />
                      ) : (
                        <RadioBox checked={isSelected(answer.id)} />
                      )}
                      {answer.text}
                    </EachOption>
                  ))
                )}
              </OptionsContainer>
            </SurveyQuestionsContainer>
            <ButtonContainer>
              <Button
                type="button"
                variant="contained"
                margintop="0px"
                bgcolor="#000000"
                textcolor="white"
                buttonsize={1}
                onClickHandler={handleNext}
                disabled={
                  isSubmitting ||
                  (currentQuestion.type === "Multi"
                    ? !tempSelections[currentQuestion.id] ||
                      tempSelections[currentQuestion.id].length === 0
                    : !tempSelections[currentQuestion.id])
                }
              >
                {currentQuestionIndex === surveyQuestions.length - 1
                  ? t(keys.finish)
                  : t(keys.next)}
              </Button>
            </ButtonContainer>
          </>
        )}
      </SurveyContainer>
    </Wrapper>
  );
};

export default InitialSurveyContainer;
